import type { ProductDataAttributes } from '../../services/productAttributes/types';
import type { PremierMarineVariantMetadata } from './types';
import { API_NAME_TO_PLAYCANVAS_KEY } from './types';
import { getPremierMarineVariantMetadata } from './attributeMetadataMap';
import { normalizeVividImageUrl } from '../../utils/playcanvasConfig';

// ── Types matching the Redux settings shape ─────────────────────

/** A single selectable option inside a settings group. */
export interface SettingValue {
  id: string;               // e.g. "railing__Black_Hammered"
  name: string;             // display name (spaces, no underscores)
  materialName: string;     // PlayCanvas value for setConfig()
  propertyKey: string;      // PlayCanvas property key
  imgUrl: string | null;    // swatch image (3-tier fallback)
  visible: boolean;         // always true for enabled variants
  /** CMS variant id (for analytics / future price lookup) */
  variantId: number;
  /** CMS description, if any */
  description: string | null;
}

/** A settings group (accordion section in the UI). */
export interface SettingGroup {
  id: string;               // section id (e.g. "rail_color__section")
  name: string;             // display label (e.g. "Rail Color")
  propertyKey: string;      // PlayCanvas key
  activeId: string | null;
  infoActiveElement: SettingValue | null;
  values: SettingValue[];
}

// ── Placeholder image ───────────────────────────────────────────

const PLACEHOLDER_IMG: string | null = null; // replace with '/img/placeholder.svg' if available

// ── 3-tier image fallback ───────────────────────────────────────

function resolveImage(
  apiImage: string | null,
  metadata: PremierMarineVariantMetadata,
): string | null {
  // 1. CMS image from API (normalized to CDN URL)
  if (apiImage) return normalizeVividImageUrl(apiImage) ?? null;
  // 2. Local icon from metadata map
  if (metadata.iconUrl) return metadata.iconUrl;
  // 3. Placeholder
  return PLACEHOLDER_IMG;
}

// ── Main builder ────────────────────────────────────────────────

/**
 * Build Redux-compatible settings array from Vivid API response,
 * enriched with project-specific metadata.
 *
 * This replaces the hardcoded `INIT_PLAYCANVAS_SETTINGS` reducer logic
 * that previously relied on `availableOptions.json` + `PLAYCANVAS_IMAGE_MAP`.
 *
 * @param apiAttributes  — output of `ProductAttributesService.getAttributes()`
 *                          keyed by API option name (e.g. "Rail Color")
 * @param currentConfig  — current PlayCanvas config snapshot (for active selection)
 *
 * @returns Array of SettingGroup objects ready to be dispatched to Redux.
 *
 * @example
 * ```ts
 * const apiAttrs = await premierMarineService.getAttributes(productId);
 * const config   = window.configurator.getConfig();
 * const settings = buildInitialAttributesFromAPI(apiAttrs, config);
 * dispatch(initSettingsFromAPI({ settings, boatType }));
 * ```
 */
export function buildInitialAttributesFromAPI(
  apiAttributes: Record<string, ProductDataAttributes>,
  currentConfig: Record<string, string | null | undefined>,
  boatType?: string,
): SettingGroup[] {
  const settings: SettingGroup[] = [];

  for (const [apiName, attrData] of Object.entries(apiAttributes)) {
    // ── Special case: "Accent Color" maps to different PlayCanvas keys per boat ──
    // PlayCanvas uses `accentColor` for intrigue, `furnitureAccentColor` for all others.
    let playcanvasKey: string | undefined;
    let metadataLookupName: string;

    if (apiName === 'Accent Color' && boatType === 'intrigue') {
      playcanvasKey = 'accentColor';
      metadataLookupName = 'Accent Color (intrigue)';
    } else {
      playcanvasKey = API_NAME_TO_PLAYCANVAS_KEY[apiName];
      metadataLookupName = apiName;
    }

    // Skip attributes we don't recognise
    if (!playcanvasKey) continue;

    // playcanvasKey is guaranteed to be a string after the guard above
    const pcKey: string = playcanvasKey;

    // Only keep enabled variants (API uses enabled flag instead of per-boat arrays)
    const enabledVariants = attrData.values.filter((v) => v.enabled);
    if (enabledVariants.length === 0) continue;

    // Get attribute-level metadata from the first variant (all share same group fields)
    const sampleMeta = getPremierMarineVariantMetadata(metadataLookupName, enabledVariants[0].name);

    const values: SettingValue[] = enabledVariants.map((variant) => {
      const meta = getPremierMarineVariantMetadata(metadataLookupName, variant.name);

      return {
        id: meta.variantKey ?? `${pcKey}__${variant.name}`,
        name: variant.name.replace(/_/g, ' '),
        materialName: meta.playcanvasMaterialName,
        propertyKey: pcKey,
        imgUrl: resolveImage(variant.image, meta),
        visible: true,
        variantId: variant.id,
        description: variant.description,
      };
    });

    // Determine which value is currently active in PlayCanvas
    const currentValue = currentConfig[pcKey] ?? null;
    const activeVal =
      values.find((v) => v.materialName === currentValue) ?? values[0];

    settings.push({
      id: sampleMeta.sectionId,
      name: sampleMeta.displayLabel,
      propertyKey: pcKey,
      activeId: activeVal?.id ?? null,
      infoActiveElement: activeVal ?? null,
      values,
    });
  }

  return settings;
}
